/**
 * Piece registry: hand-authored, geometry-calibrated defs for the Marble Kit
 * path pieces the generator can realize, plus derived reversed variants.
 *
 * All numbers here were measured from the GLBs (channel floor = base + 0.25,
 * walls to base + 0.5, ramp tier deltas, curve arc radii). Defs are authored
 * in "entry frame" — see geom.ts. validateRegistry() in tests asserts the
 * internal consistency of every def.
 */
import type { CurveSeg, Dir, Vec3 } from "../shared/types.ts";
import {
  DIR_STEP,
  moveLabel,
  opposite,
  reverseSeg,
  rotP,
  segPoint,
  turnLeft,
  turnRight,
} from "./geom.ts";

export interface PortPose {
  /** Port position in entry frame (y = base height, not channel floor). */
  pos: Vec3;
  /** Direction of travel through the port. */
  dir: Dir;
  /** Base height at the port (multiple of 0.25 in practice). */
  baseY: number;
}

/** Secondary mesh of a composite piece, posed in entry frame. */
export interface Overlay {
  id: string;
  offset: Vec3;
  /** Extra quarter-turns on top of the placement rotation. */
  rot?: Dir;
  /** Render this mesh x-flipped (independent of the def's own flag). */
  mirrored?: boolean;
}

export interface PieceDef {
  /** Piece identity (style weights, reservations key on this). */
  id: string;
  /** GLB mesh to place, when it differs from `id` (e.g. tunnel = straight). */
  meshId?: string;
  /** Extra meshes stacked on the base mesh, offsets in entry frame. */
  overlays?: readonly Overlay[];
  family: string;
  /** Skeleton moves consumed, e.g. "F", "L", "FLF", "FFLFF". */
  moves: string;
  /** Exit base height − entry base height. */
  deckDelta: number;
  /** Channel floor above local base at entry/exit (0.25 for most pieces). */
  floorIn: number;
  floorOut: number;
  /** GLB origin position in entry frame. */
  meshOffset: Vec3;
  /** Extra quarter-turns to apply to the mesh on top of placement rotation. */
  meshRot: Dir;
  /** All cells occupied (entry frame offsets; entry cell = [0,0]). */
  footprint: readonly (readonly [number, number])[];
  /** Cells the channel actually passes through, in order (supports go here). */
  supportCells: readonly (readonly [number, number])[];
  /** Centerline at channel-floor height, entry port → exit port. */
  centerline: readonly CurveSeg[];
  /**
   * Alternative path through the piece (a split–merge diamond's other
   * branch). Same entry/exit ports as `centerline`; the marble alternates
   * per lap.
   */
  altCenterline?: readonly CurveSeg[];
  /** Floor-pierce centers at floor height (entry frame): the collision
   * ribbon opens the kit's round r=0.25 aperture at each (T2). */
  holes?: readonly Vec3[];
  /** Grade-crossing collision role (T2): "cross" emits the authored
   * 4-way junction composite; "quiet" (the noMesh phantom) suppresses
   * its ribbon — the shared deck comes from the mesh visit. */
  junction?: "cross" | "quiet";
  /** Occupied y band above local base (for collision). */
  heightSpan: readonly [number, number];
  /** GLB id of the closed-underside variant, if the kit has one. */
  solidId?: string;
  baseWeight: number;
  /** True when this def was derived by reversing another. */
  reversed?: boolean;
  /** Render the base mesh x-flipped (defs derived via mirrorDef). */
  mirrored?: boolean;
  /** Channel width at the entry/exit port (wide = 1.7-cell channel). */
  widthIn?: "narrow" | "wide";
  widthOut?: "narrow" | "wide";
  /** Only placeable via reservations (feature planner), never free WFC. */
  reservedOnly?: boolean;
  /**
   * Emit no mesh placement — the piece's geometry is provided by another
   * window sharing its cell (the second visit of a grade crossing). The
   * centerline still contributes to the loop.
   */
  noMesh?: boolean;
  /**
   * Channel doesn't hug cell-center lanes on its F cells (waves, bends,
   * s-curves, wide pieces) — elevated supports use plain columns snapped to
   * the centerline instead of cell-center cradle stacks.
   */
  columnSupports?: boolean;
  /**
   * Plate tiles (short s-curve jogs): the mesh is a square slab with the
   * channel carved diagonally through, leaving triangular platform wings.
   * These cells (entry frame, OUTSIDE the footprint) must carry track for
   * the wings to read as embedded deck — the WFC skips the candidate when
   * any wing cell is bare grass. Standard corners stay the default.
   */
  plateSides?: readonly (readonly [number, number])[];
  /**
   * The solid variant fills the full footprint square(s) beyond the pipe
   * (corner/curve blocks), unlike open forms whose outline is contour-cut.
   * Assembly uses the solid ONLY when that extended surface abuts other
   * same-height deck; otherwise the pipe-only open form is placed (user
   * rule: no solid block corners jutting where nothing touches them).
   */
  solidExtends?: boolean;
}

const F = 0.25; // standard channel floor above base
const W = 0.5; // wall top above base

function line(a: Vec3, b: Vec3): CurveSeg {
  return { kind: "line", a, b };
}
function cubic(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3): CurveSeg {
  return { kind: "cubic", p: [p0, p1, p2, p3] };
}

/** Quarter arc for an L-turn with channel radius `radius`: enters at
 * (0, ·, −0.5) heading +z, exits at (−radius, ·, −0.5+radius) heading −x.
 * Arc center sits at (−radius, ·, −0.5) — calibrated against corner /
 * curve / curve-large geometry. */
function leftArc(radius: number, yIn: number, yOut: number): CurveSeg {
  return {
    kind: "arc",
    center: [-radius, 0, -0.5],
    radius,
    a0: 0,
    a1: Math.PI / 2,
    y0: yIn,
    y1: yOut,
  };
}

/** Build the defs table. Kept as a function for testability. */
function buildDefs(): PieceDef[] {
  const defs: PieceDef[] = [];

  // --- straights -----------------------------------------------------------
  defs.push({
    id: "straight",
    family: "straight",
    moves: "F",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [0, 0, 0],
    meshRot: 0,
    footprint: [[0, 0]],
    supportCells: [[0, 0]],
    centerline: [line([0, F, -0.5], [0, F, 0.5])],
    heightSpan: [0, W],
    baseWeight: 10,
  });

  // tunnel: the GLB is only the arch shell (top half) — it seats on a
  // straight with its feet on the wall tops (measured against Sample.png).
  defs.push({
    id: "tunnel",
    meshId: "straight",
    overlays: [{ id: "tunnel", offset: [0, W, 0] }],
    family: "tunnel",
    moves: "F",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [0, 0, 0],
    meshRot: 0,
    footprint: [[0, 0]],
    supportCells: [[0, 0]],
    centerline: [line([0, F, -0.5], [0, F, 0.5])],
    heightSpan: [0, W + 0.6],
    baseWeight: 1.5,
  });

  // straight-hole: a straight whose floor is pierced — on a bridge deck the
  // marble has a CHANCE to fall through onto the channel below (user rule:
  // holes require track below, guaranteed here because only bridge-crossing
  // reservations place it). The centerline is the roll-over path; the fall
  // is the client's per-lap deviation.
  defs.push({
    id: "straight-hole",
    family: "straight",
    moves: "F",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [0, 0, 0],
    meshRot: 0,
    footprint: [[0, 0]],
    supportCells: [[0, 0]],
    centerline: [line([0, F, -0.5], [0, F, 0.5])],
    // Measured: round bore r 0.25 at the cell center — the collision
    // ribbon opens a real aperture there (T2).
    holes: [[0, F, 0]],
    heightSpan: [0, W],
    reservedOnly: true,
    baseWeight: 1,
  });

  // Tunnel runs: contiguous covered stretches (the M1 singleton reads as a
  // lone gate; a run reads as an actual tunnel). Composite of one straight +
  // arch per cell — same measured seating as the singleton.
  for (const len of [2, 3]) {
    const cells: [number, number][] = [];
    const overlays: Overlay[] = [{ id: "tunnel", offset: [0, W, 0] }];
    for (let j = 0; j < len; j++) {
      cells.push([0, j]);
      if (j > 0) {
        overlays.push({ id: "straight", offset: [0, 0, j] });
        overlays.push({ id: "tunnel", offset: [0, W, j] });
      }
    }
    defs.push({
      id: `tunnel-run-${len}`,
      meshId: "straight",
      overlays,
      family: "tunnel",
      moves: "F".repeat(len),
      deckDelta: 0,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 0],
      meshRot: 0,
      footprint: cells,
      supportCells: cells,
      centerline: [line([0, F, -0.5], [0, F, len - 0.5])],
      heightSpan: [0, W + 0.6],
      baseWeight: len === 2 ? 2.5 : 1.5,
    });
  }

  // Drop-stacks (user design): an end-hole stacked FLUSH on a plain end
  // piece (0.5 grid — nothing floats). The marble enters the upper port,
  // falls through the hole into the cup below, and exits the receiver's
  // port — a guaranteed vertical transfer. The channel network is
  // deliberately non-connected here; the LOOP closes through the fall (the
  // centerline carries a vertical segment, and the gravity model makes the
  // marble properly plunge). One cell, deckDelta −0.5; the exit port may
  // continue straight or turn (the cup is round): F/L/R variants, rounded
  // and square flavors. Reservation-only, placed by the descent vocabulary
  // and the corner-drop planner.
  for (const flavor of ["rounded", "square"] as const) {
    for (
      const [turn, moves, exitRot] of [
        ["f", "F", 2],
        ["l", "L", 1],
        ["r", "R", 3],
      ] as const
    ) {
      const exit = turn === "f"
        ? [0, 0.5]
        : turn === "l"
        ? [-0.5, 0]
        : [0.5, 0];
      defs.push({
        id: `drop-stack-${flavor}-${turn}`,
        meshId: `end-hole-${flavor}`,
        overlays: [
          // Receiver cup at ground; its port (local −z) faces the exit.
          { id: `end-${flavor}`, offset: [0, -0.5, 0], rot: exitRot },
        ],
        family: "drop",
        moves,
        deckDelta: -0.5,
        floorIn: F,
        floorOut: F,
        meshOffset: [0, 0, 0],
        meshRot: 0,
        footprint: [[0, 0]],
        supportCells: [[0, 0]],
        centerline: [
          line([0, F, -0.5], [0, F, 0]),
          line([0, F, 0], [0, F - 0.5, 0]),
          line([0, F - 0.5, 0], [exit[0], F - 0.5, exit[1]]),
        ],
        heightSpan: [-0.5, W],
        reservedOnly: true,
        baseWeight: 2,
      });
    }
  }

  // Grade crossing: 4-way symmetric block (measured: the straight's port
  // signature on all four faces, floor 0.25). The ring visits its cell twice
  // (perpendicular, same height, pierce mutation); the first visit places
  // the mesh, the second is the noMesh phantom so the loop centerline still
  // chains. Reservation-only — free WFC never crosses itself.
  for (const phantom of [false, true]) {
    defs.push({
      id: phantom ? "cross-over" : "cross",
      ...(phantom ? { meshId: "cross", noMesh: true } : {}),
      // Junction collision (T2): the mesh visit emits the 4-way
      // composite; the phantom stays quiet — its route's floor IS the
      // shared deck.
      junction: phantom ? "quiet" : "cross",
      family: "cross",
      moves: "F",
      deckDelta: 0,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 0],
      meshRot: 0,
      footprint: [[0, 0]],
      supportCells: [[0, 0]],
      centerline: [line([0, F, -0.5], [0, F, 0.5])],
      heightSpan: [0, W],
      reservedOnly: true,
      baseWeight: 1,
    });
  }

  // --- turns ---------------------------------------------------------------
  // corner: 1×1, quarter arc r=0.5, turns south→west (L).
  defs.push({
    id: "corner",
    family: "corner",
    moves: "L",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [0, 0, 0],
    meshRot: 0,
    footprint: [[0, 0]],
    supportCells: [[0, 0]],
    centerline: [leftArc(0.5, F, F)],
    heightSpan: [0, W],
    solidId: "corner-solid",
    solidExtends: true,
    baseWeight: 6,
  });

  // curve: 2×2 block, entry cell south-east of block; window F,L,F.
  defs.push({
    id: "curve",
    family: "curve",
    moves: "FLF",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [-0.5, 0, 0.5],
    meshRot: 0,
    footprint: [[0, 0], [0, 1], [-1, 1], [-1, 0]],
    supportCells: [[0, 0], [0, 1], [-1, 1]],
    centerline: [leftArc(1.5, F, F)],
    heightSpan: [0, W],
    solidId: "curve-solid",
    solidExtends: true,
    baseWeight: 5,
  });

  // curve-large: 3×3 block; window F,F,L,F,F.
  defs.push({
    id: "curve-large",
    family: "curve",
    moves: "FFLFF",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [-1, 0, 1],
    meshRot: 0,
    footprint: [
      [0, 0],
      [0, 1],
      [0, 2],
      [-1, 0],
      [-1, 1],
      [-1, 2],
      [-2, 0],
      [-2, 1],
      [-2, 2],
    ],
    supportCells: [[0, 0], [0, 1], [-1, 1], [-1, 2], [-2, 2]],
    centerline: [leftArc(2.5, F, F)],
    heightSpan: [0, W],
    solidId: "curve-solid-large",
    solidExtends: true,
    baseWeight: 3,
  });

  // --- climbs --------------------------------------------------------------
  // slant a–d exist, but only b/c/d have deltas on the 0.5 support grid.
  const slantDelta = { b: 0.5, c: 1.0, d: 1.5 } as const;
  for (const tier of ["b", "c", "d"] as const) {
    const d = slantDelta[tier];
    defs.push({
      id: `slant-${tier}`,
      family: "slant",
      moves: "F",
      deckDelta: d,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 0],
      meshRot: 0,
      footprint: [[0, 0]],
      supportCells: [[0, 0]],
      centerline: [line([0, F, -0.5], [0, F + d, 0.5])],
      heightSpan: [0, W + d],
      solidId: `slant-solid-${tier}`,
      baseWeight: tier === "b" ? 3 : tier === "c" ? 2 : 1,
    });
    defs.push({
      id: `slant-long-${tier}`,
      family: "slant",
      moves: "FF",
      deckDelta: d,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 0.5],
      meshRot: 0,
      footprint: [[0, 0], [0, 1]],
      supportCells: [[0, 0], [0, 1]],
      centerline: [line([0, F, -0.5], [0, F + d, 1.5])],
      heightSpan: [0, W + d],
      solidId: `slant-solid-long-${tier}`,
      baseWeight: 3,
    });
    // ramp-long: same span/delta, S-eased profile.
    defs.push({
      id: `ramp-long-${tier}`,
      family: "ramp",
      moves: "FF",
      deckDelta: d,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 0.5],
      meshRot: 0,
      footprint: [[0, 0], [0, 1]],
      supportCells: [[0, 0], [0, 1]],
      centerline: [
        cubic(
          [0, F, -0.5],
          [0, F, 0.3],
          [0, F + d, 0.7],
          [0, F + d, 1.5],
        ),
      ],
      heightSpan: [0, W + d],
      solidId: `ramp-long-solid-${tier}`,
      baseWeight: 4,
    });
  }

  // ramp-start + ramp-end pairs: each rises the tier delta over one cell,
  // eased. Modeled as a single 2-cell composite is unnecessary — each is a
  // valid 1-cell piece; the elevation system reserves them as pairs so the
  // mid-climb base stays on the vocabulary's grid. Tier deltas measured:
  // b 0.25, c 0.5, d 0.75 (a = 0.125 is off every usable grid — skipped).
  for (
    const [tier, d, weight] of [["b", 0.25, 2], ["c", 0.5, 3], ["d", 0.75, 2]]
      .map((t) => t as [string, number, number])
  ) {
    defs.push({
      id: `ramp-start-${tier}`,
      family: "ramp",
      moves: "F",
      deckDelta: d,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 0],
      meshRot: 0,
      footprint: [[0, 0]],
      supportCells: [[0, 0]],
      centerline: [
        cubic(
          [0, F, -0.5],
          [0, F, -0.1],
          [0, F + d * 0.56, 0.28],
          [0, F + d, 0.5],
        ),
      ],
      heightSpan: [0, W + d],
      solidId: `ramp-start-solid-${tier}`,
      baseWeight: weight,
    });
    defs.push({
      id: `ramp-end-${tier}`,
      family: "ramp",
      moves: "F",
      deckDelta: d,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 0],
      meshRot: 0,
      footprint: [[0, 0]],
      supportCells: [[0, 0]],
      centerline: [
        cubic(
          [0, F, -0.5],
          [0, F + d * 0.44, -0.28],
          [0, F + d, 0.1],
          [0, F + d, 0.5],
        ),
      ],
      heightSpan: [0, W + d],
      solidId: `ramp-end-solid-${tier}`,
      baseWeight: weight,
    });
  }

  // --- bends (U-turns) -----------------------------------------------------
  // Measured: both ports on the authored −x face at cell centers, and the
  // semicircle's diameter lies exactly ON the port face (zero lead-in), arc
  // center at (bboxMin.x, 0), radii 0.5/1.5/2.5, floor 0.25 throughout.
  // meshRot 3 turns the authored +x entry into the entry frame's +z.
  defs.push({
    id: "bend",
    family: "bend",
    moves: "LL",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [-0.5, 0, 0],
    meshRot: 3,
    footprint: [[0, 0], [-1, 0]],
    supportCells: [[0, 0], [-1, 0]],
    centerline: [
      {
        kind: "arc",
        center: [-0.5, 0, -0.5],
        radius: 0.5,
        a0: 0,
        a1: Math.PI,
        y0: F,
        y1: F,
      },
    ],
    heightSpan: [0, W],
    baseWeight: 14,
  });
  defs.push({
    id: "bend-medium",
    family: "bend",
    moves: "FLFFLF",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [-1.5, 0, 0.5],
    meshRot: 3,
    footprint: [
      [0, 0],
      [0, 1],
      [-1, 1],
      [-2, 1],
      [-3, 1],
      [-3, 0],
      [-1, 0],
      [-2, 0],
    ],
    supportCells: [[0, 0], [0, 1], [-1, 1], [-2, 1], [-3, 1], [-3, 0]],
    centerline: [
      {
        kind: "arc",
        center: [-1.5, 0, -0.5],
        radius: 1.5,
        a0: 0,
        a1: Math.PI,
        y0: F,
        y1: F,
      },
    ],
    heightSpan: [0, W],
    solidId: "bend-solid-medium",
    columnSupports: true,
    baseWeight: 16,
  });
  {
    const footprint: [number, number][] = [];
    for (let x = -5; x <= 0; x++) {
      for (let z = 0; z <= 2; z++) footprint.push([x, z]);
    }
    defs.push({
      id: "bend-large",
      family: "bend",
      moves: "FFLFFFFLFF",
      deckDelta: 0,
      floorIn: F,
      floorOut: F,
      meshOffset: [-2.5, 0, 1.0],
      meshRot: 3,
      footprint,
      supportCells: [
        [0, 0],
        [0, 1],
        [0, 2],
        [-1, 2],
        [-2, 2],
        [-3, 2],
        [-4, 2],
        [-5, 2],
        [-5, 1],
        [-5, 0],
      ],
      centerline: [
        {
          kind: "arc",
          center: [-2.5, 0, -0.5],
          radius: 2.5,
          a0: 0,
          a1: Math.PI,
          y0: F,
          y1: F,
        },
      ],
      heightSpan: [0, W],
      solidId: "bend-solid-large",
      columnSupports: true,
      baseWeight: 16,
    });
  }

  // --- helixes (descending turns) ------------------------------------------
  // Measured: each helix is exactly its flat cousin's arc — curve /
  // curve-large for quarters, bend-medium / bend-large semicircles for
  // halves (same ports, radii, footprints) — with the channel floor
  // descending LINEARLY IN ANGLE. Quarters drop 0.5, halves drop 1.0; the
  // mesh is grounded on its exit side, so meshOffset.y = −drop puts the
  // entry base at the entry frame's 0. Forward defs are DESCENTS
  // (deckDelta < 0 keeps them out of free WFC collapse); the elevation
  // planner reserves them at turn windows ("corner drops"). Reversed
  // variants are gentle spiral climbs (~0.21/cell). Native right-handed
  // GLBs are exact mirrors — same convention as s-curves.
  const helixQuarterLeft: PieceDef = {
    id: "helix-quarter-left",
    family: "helix",
    moves: "FLF",
    deckDelta: -0.5,
    floorIn: F,
    floorOut: F,
    meshOffset: [-0.5, -0.5, 0.5],
    meshRot: 0,
    footprint: [[0, 0], [0, 1], [-1, 1], [-1, 0]],
    supportCells: [[0, 0], [0, 1], [-1, 1]],
    centerline: [leftArc(1.5, F, F - 0.5)],
    heightSpan: [-0.5, W],
    columnSupports: true,
    baseWeight: 8,
  };
  defs.push(helixQuarterLeft);
  defs.push({
    ...mirrorDef(helixQuarterLeft, "helix-quarter-right"),
    meshId: undefined,
    mirrored: false,
  });
  const helixLargeQuarterLeft: PieceDef = {
    id: "helix-large-quarter-left",
    family: "helix",
    moves: "FFLFF",
    deckDelta: -0.5,
    floorIn: F,
    floorOut: F,
    meshOffset: [-1, -0.5, 1],
    meshRot: 0,
    footprint: [
      [0, 0],
      [0, 1],
      [0, 2],
      [-1, 0],
      [-1, 1],
      [-1, 2],
      [-2, 0],
      [-2, 1],
      [-2, 2],
    ],
    supportCells: [[0, 0], [0, 1], [-1, 1], [-1, 2], [-2, 2]],
    centerline: [leftArc(2.5, F, F - 0.5)],
    heightSpan: [-0.5, W],
    columnSupports: true,
    baseWeight: 6,
  };
  defs.push(helixLargeQuarterLeft);
  defs.push({
    ...mirrorDef(helixLargeQuarterLeft, "helix-large-quarter-right"),
    meshId: undefined,
    mirrored: false,
  });
  const helixHalfLeft: PieceDef = {
    id: "helix-half-left",
    family: "helix",
    moves: "FLFFLF",
    deckDelta: -1.0,
    floorIn: F,
    floorOut: F,
    meshOffset: [-1.5, -1.0, 0.5],
    meshRot: 3,
    footprint: [
      [0, 0],
      [0, 1],
      [-1, 1],
      [-2, 1],
      [-3, 1],
      [-3, 0],
      [-1, 0],
      [-2, 0],
    ],
    supportCells: [[0, 0], [0, 1], [-1, 1], [-2, 1], [-3, 1], [-3, 0]],
    centerline: [
      {
        kind: "arc",
        center: [-1.5, 0, -0.5],
        radius: 1.5,
        a0: 0,
        a1: Math.PI,
        y0: F,
        y1: F - 1.0,
      },
    ],
    heightSpan: [-1.0, W],
    columnSupports: true,
    baseWeight: 8,
  };
  defs.push(helixHalfLeft);
  defs.push({
    ...mirrorDef(helixHalfLeft, "helix-half-right"),
    meshId: undefined,
    mirrored: false,
  });
  {
    const footprint: [number, number][] = [];
    for (let x = -5; x <= 0; x++) {
      for (let z = 0; z <= 2; z++) footprint.push([x, z]);
    }
    const helixLargeHalfLeft: PieceDef = {
      id: "helix-large-half-left",
      family: "helix",
      moves: "FFLFFFFLFF",
      deckDelta: -1.0,
      floorIn: F,
      floorOut: F,
      meshOffset: [-2.5, -1.0, 1.0],
      meshRot: 3,
      footprint,
      supportCells: [
        [0, 0],
        [0, 1],
        [0, 2],
        [-1, 2],
        [-2, 2],
        [-3, 2],
        [-4, 2],
        [-5, 2],
        [-5, 1],
        [-5, 0],
      ],
      centerline: [
        {
          kind: "arc",
          center: [-2.5, 0, -0.5],
          radius: 2.5,
          a0: 0,
          a1: Math.PI,
          y0: F,
          y1: F - 1.0,
        },
      ],
      heightSpan: [-1.0, W],
      columnSupports: true,
      baseWeight: 6,
    };
    defs.push(helixLargeHalfLeft);
    defs.push({
      ...mirrorDef(helixLargeHalfLeft, "helix-large-half-right"),
      meshId: undefined,
      mirrored: false,
    });
  }

  // Split–merge diamonds: split-double fans the channel into two parallel
  // branches one lane out each side (measured: center entry, exits at x±1
  // heading +z over a 3×2 block); N straights per branch; a 180°-rotated
  // split-double merges them back. One composite reserved def per N — the
  // feature planner places them on straight runs with both flanks free.
  // centerline = LEFT branch, altCenterline = RIGHT; the client alternates
  // per lap. Branch jogs are z-linear-third cubics (the lane-shift law).
  for (const N of [1, 2, 3]) {
    const len = 4 + N;
    const footprint: [number, number][] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let j = 0; j < len; j++) footprint.push([dx, j]);
    }
    const overlays: Overlay[] = [
      { id: "split-double", offset: [0, 0, 0.5] },
      { id: "split-double", offset: [0, 0, N + 2.5], rot: 2 },
    ];
    for (let j = 0; j < N; j++) {
      overlays.push({ id: "straight", offset: [-1, 0, 2 + j] });
      overlays.push({ id: "straight", offset: [1, 0, 2 + j] });
    }
    const branch = (side: -1 | 1): CurveSeg[] => [
      cubic(
        [0, F, -0.5],
        [0, F, 2 / 3 - 0.5],
        [side, F, 4 / 3 - 0.5],
        [side, F, 1.5],
      ),
      line([side, F, 1.5], [side, F, N + 1.5]),
      cubic(
        [side, F, N + 1.5],
        [side, F, N + 1.5 + 2 / 3],
        [0, F, N + 1.5 + 4 / 3],
        [0, F, N + 3.5],
      ),
    ];
    defs.push({
      id: `diamond-${N}`,
      meshId: "split-double",
      overlays: overlays.slice(1),
      family: "split",
      moves: "F".repeat(len),
      deckDelta: 0,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 0.5],
      meshRot: 0,
      footprint,
      supportCells: footprint.filter(([dx]) => dx === 0),
      centerline: branch(-1),
      altCenterline: branch(1),
      heightSpan: [0, W],
      columnSupports: true,
      reservedOnly: true,
      baseWeight: 4,
    });
  }

  // Full helixes: a 360° spiral (drop 2.0, linear in angle) whose entry and
  // exit share ONE port edge at the block's mid-face — the ring passes under
  // the spiral's own start. One reserved ensemble window covers it all:
  // approach corner (top level, corner overlay), the perimeter spiral, and
  // the underpass straights (ground level, straight overlays) back out
  // through the first spiral cells. The window's moves REVISIT its first
  // cells (the topology helix-loop mutation makes the matching ring), so
  // footprints dedup and the mesh provides both levels. Mesh grounded at the
  // exit side: meshOffset.y = −2. Only the elevation planner reserves these
  // (with a mandatory 2.0 climb elsewhere on the ring).
  for (const large of [false, true]) {
    const R = large ? 2.5 : 1.5; // spiral lane radius
    const N = large ? 6 : 4; // block is N×N cells
    const side = N - 1;
    const under = N / 2; // underpass length in cells (2 small, 3 large)
    // Walk: A(0,0) R turn, ccw around the block perimeter (F…L per side),
    // then back through A and the first cells (the underpass), exiting +x.
    let moves = "R";
    moves += "F".repeat(under - 1) + "L"; // A → along +x to the corner
    for (let s = 0; s < 3; s++) moves += "F".repeat(side - 1) + "L";
    moves += "F".repeat(side - 1 - under); // return leg, short of A
    moves += "F"; // through A again
    moves += "F".repeat(under); // underpass B, C(, D)
    // Trace cells for footprint/supports.
    const walk: [number, number][] = [];
    let cx = 0, cz = 0, dir = 1;
    for (const m of moves) {
      dir = m === "F" ? dir : m === "L" ? (dir + 1) % 4 : (dir + 3) % 4;
      walk.push([cx, cz]);
      cx += [1, 0, -1, 0][dir];
      cz += [0, 1, 0, -1][dir];
    }
    const uniq = new Map<string, [number, number]>();
    for (const c of walk) uniq.set(`${c[0]},${c[1]}`, c);
    // Interior cells (the helix's central column) complete the footprint.
    const xs = [...uniq.values()].map((c) => c[0]);
    const zs = [...uniq.values()].map((c) => c[1]);
    const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
    const [z0, z1] = [Math.min(...zs), Math.max(...zs)];
    const footprint: [number, number][] = [];
    for (let fx = x0; fx <= x1; fx++) {
      for (let fz = z0; fz <= z1; fz++) footprint.push([fx, fz]);
    }
    const overlays: Overlay[] = [
      // Approach turn at the top — mirrored corner = R turn.
      { id: "corner", offset: [0, 0, 0], mirrored: true },
    ];
    for (let j = 1; j <= under; j++) {
      overlays.push({ id: "straight", offset: [j, -2, 0], rot: 1 });
    }
    const def: PieceDef = {
      id: large ? "helix-large-left" : "helix-left",
      overlays,
      family: "helix",
      moves,
      deckDelta: -2,
      floorIn: F,
      floorOut: F,
      meshOffset: [0.5, -2, R],
      meshRot: 2,
      footprint,
      supportCells: [...uniq.values()],
      centerline: [
        // Approach corner: R quarter, r 0.5, flat at the top level.
        {
          kind: "arc",
          center: [0.5, 0, -0.5],
          radius: 0.5,
          a0: Math.PI,
          a1: Math.PI / 2,
          y0: F,
          y1: F,
        },
        // The spiral: full ccw turn, floor linear in angle (measured).
        {
          kind: "arc",
          center: [0.5, 0, R],
          radius: R,
          a0: -Math.PI / 2,
          a1: Math.PI * 1.5,
          y0: F,
          y1: F - 2,
        },
        line([0.5, F - 2, 0], [under + 0.5, F - 2, 0]),
      ],
      heightSpan: [-2, W],
      columnSupports: true,
      reservedOnly: true,
      baseWeight: 4,
    };
    defs.push(def);
    defs.push({
      ...mirrorDef(def, large ? "helix-large-right" : "helix-right"),
      meshId: undefined,
      mirrored: false,
    });
  }

  // --- s-curves ------------------------------------------------------------
  // Measured: two mirrored tangent-continuous 90° arcs of the curve /
  // curve-large lane radii; the lane heading is momentarily perpendicular to
  // the travel axis at the inflection. Point-symmetric, so the reversed
  // variant collapses onto the forward def. Kenney's "left" jogs +x in the
  // entry frame — our "R" chirality; the native right meshes are exact
  // mirrors, so their defs are derived via mirrorDef with the flip cleared.
  const sCurveLeft: PieceDef = {
    id: "s-curve-left",
    family: "s-curve",
    moves: "FRFFLF",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [1.5, 0, 1.0],
    meshRot: 0,
    footprint: [
      [0, 0],
      [0, 1],
      [1, 0],
      [1, 1],
      [2, 1],
      [2, 2],
      [3, 1],
      [3, 2],
    ],
    supportCells: [[0, 0], [0, 1], [1, 1], [2, 1], [3, 1], [3, 2]],
    centerline: [
      {
        kind: "arc",
        center: [1.5, 0, -0.5],
        radius: 1.5,
        a0: Math.PI,
        a1: Math.PI / 2,
        y0: F,
        y1: F,
      },
      {
        kind: "arc",
        center: [1.5, 0, 2.5],
        radius: 1.5,
        a0: -Math.PI / 2,
        a1: 0,
        y0: F,
        y1: F,
      },
    ],
    heightSpan: [0, W],
    columnSupports: true,
    baseWeight: 12,
  };
  defs.push(sCurveLeft);
  defs.push({
    ...mirrorDef(sCurveLeft, "s-curve-right"),
    meshId: undefined,
    mirrored: false,
  });
  const sCurveLeftLarge: PieceDef = {
    id: "s-curve-left-large",
    family: "s-curve",
    moves: "FFRFFFFLFF",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [2.5, 0, 2.0],
    meshRot: 0,
    footprint: [
      [0, 0],
      [0, 1],
      [0, 2],
      [1, 0],
      [1, 1],
      [1, 2],
      [2, 1],
      [2, 2],
      [3, 2],
      [3, 3],
      [4, 2],
      [4, 3],
      [4, 4],
      [5, 2],
      [5, 3],
      [5, 4],
    ],
    supportCells: [
      [0, 0],
      [0, 1],
      [0, 2],
      [1, 2],
      [2, 2],
      [3, 2],
      [4, 2],
      [5, 2],
      [5, 3],
      [5, 4],
    ],
    centerline: [
      {
        kind: "arc",
        center: [2.5, 0, -0.5],
        radius: 2.5,
        a0: Math.PI,
        a1: Math.PI / 2,
        y0: F,
        y1: F,
      },
      {
        kind: "arc",
        center: [2.5, 0, 4.5],
        radius: 2.5,
        a0: -Math.PI / 2,
        a1: 0,
        y0: F,
        y1: F,
      },
    ],
    heightSpan: [0, W],
    columnSupports: true,
    baseWeight: 14,
  };
  defs.push(sCurveLeftLarge);
  defs.push({
    ...mirrorDef(sCurveLeftLarge, "s-curve-right-large"),
    meshId: undefined,
    mirrored: false,
  });

  // s-curve-short tiers: smootherstep jogs of 0.25 / 0.5 / 1.0 over two
  // cells. Only tier c is grid-aligned on its own; a/b enter as paired
  // combos (wiggle = jog out + mirrored jog back; lane-shift = two chained
  // b jogs). Cubics use z-linear control thirds ⇒ x(z) = smoothstep — close
  // enough to the measured smootherstep at these amplitudes.
  const jogSeg = (
    x0: number,
    x1: number,
    z0: number,
    z1: number,
  ): CurveSeg =>
    cubic(
      [x0, F, z0],
      [x0, F, z0 + (z1 - z0) / 3],
      [x1, F, z1 - (z1 - z0) / 3],
      [x1, F, z1],
    );
  const shortC: PieceDef = {
    id: "s-curve-short-left-c",
    family: "s-curve",
    moves: "FRL",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [0.5, 0, 0.5],
    meshRot: 0,
    footprint: [[0, 0], [0, 1], [1, 0], [1, 1]],
    supportCells: [[0, 0], [0, 1], [1, 1]],
    // Platform wings: left of the entry lane, right of the exit lane.
    plateSides: [[-1, 0], [2, 1]],
    centerline: [jogSeg(0, 1, -0.5, 1.5)],
    heightSpan: [0, W],
    columnSupports: true,
    baseWeight: 5,
  };
  defs.push(shortC);
  defs.push(mirrorDef(shortC, "s-curve-short-right-c"));

  for (const [tier, amp] of [["a", 0.125], ["b", 0.25]] as const) {
    const wiggle: PieceDef = {
      id: `wiggle-${tier}`,
      family: "s-curve",
      meshId: `s-curve-short-left-${tier}`,
      moves: "FFFF",
      deckDelta: 0,
      floorIn: F,
      floorOut: F,
      meshOffset: [amp, 0, 0.5],
      meshRot: 0,
      overlays: [
        {
          id: `s-curve-short-left-${tier}`,
          offset: [amp, 0, 2.5],
          mirrored: true,
        },
      ],
      footprint: [[0, 0], [0, 1], [0, 2], [0, 3], [1, 1], [1, 2]],
      supportCells: [[0, 0], [0, 1], [0, 2], [0, 3]],
      // b-tier plates are wide enough to read as platforms; a-tier wings
      // are sub-0.35 slivers and pass as chamfers.
      ...(tier === "b" ? { plateSides: [[-1, 0], [-1, 3]] as const } : {}),
      centerline: [
        jogSeg(0, 2 * amp, -0.5, 1.5),
        jogSeg(2 * amp, 0, 1.5, 3.5),
      ],
      heightSpan: [0, W],
      columnSupports: true,
      baseWeight: 2.5,
    };
    defs.push(wiggle);
    defs.push(mirrorDef(wiggle, `wiggle-${tier}-mirror`));
  }

  const laneShiftB: PieceDef = {
    id: "lane-shift-b",
    family: "s-curve",
    meshId: "s-curve-short-left-b",
    moves: "FRLFF",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    meshOffset: [0.25, 0, 0.5],
    meshRot: 0,
    overlays: [{ id: "s-curve-short-left-b", offset: [0.75, 0, 2.5] }],
    footprint: [[0, 0], [0, 1], [0, 2], [1, 1], [1, 2], [1, 3]],
    supportCells: [[0, 0], [0, 1], [1, 1], [1, 2], [1, 3]],
    plateSides: [[-1, 0], [2, 3]],
    centerline: [jogSeg(0, 0.5, -0.5, 1.5), jogSeg(0.5, 1, 1.5, 3.5)],
    heightSpan: [0, W],
    columnSupports: true,
    baseWeight: 3,
  };
  defs.push(laneShiftB);
  defs.push(mirrorDef(laneShiftB, "lane-shift-b-mirror"));

  // --- waves (lateral) and bumps (vertical) --------------------------------
  // Measured: waves are purely lateral, bumps purely vertical, both
  // smootherstep-eased trough→crest→trough over 4 cells with the peak at
  // mid-piece. Wave ports sit at lane −A of the mesh frame, so the mesh
  // shifts +A to put the port lanes on the cell centerline (crest at +2A).
  for (
    const [tier, amp, w] of [
      ["a", 0.125, 3],
      ["b", 0.25, 3.2],
      ["c", 0.5, 3.5],
    ] as const
  ) {
    const wave: PieceDef = {
      id: `wave-${tier}`,
      family: "wave",
      moves: "FFFF",
      deckDelta: 0,
      floorIn: F,
      floorOut: F,
      meshOffset: [amp, 0, 1.5],
      meshRot: 0,
      footprint: [[0, 0], [0, 1], [0, 2], [0, 3], [1, 1], [1, 2]],
      supportCells: [[0, 0], [0, 1], [0, 2], [0, 3]],
      centerline: [
        jogSeg(0, 2 * amp, -0.5, 1.5),
        jogSeg(2 * amp, 0, 1.5, 3.5),
      ],
      heightSpan: [0, W],
      solidId: `wave-solid-${tier}`,
      columnSupports: true,
      baseWeight: w,
    };
    defs.push(wave);
    defs.push(mirrorDef(wave, `wave-${tier}-mirror`));
  }

  const bumpSeg = (y0: number, y1: number, z0: number, z1: number): CurveSeg =>
    cubic(
      [0, y0, z0],
      [0, y0, z0 + (z1 - z0) / 3],
      [0, y1, z1 - (z1 - z0) / 3],
      [0, y1, z1],
    );
  for (
    const [tier, rise, w] of [
      ["a", 0.25, 4],
      ["b", 0.5, 3.5],
      ["c", 1.0, 2],
      ["d", 1.5, 1],
    ] as const
  ) {
    defs.push({
      id: `bump-${tier}`,
      family: "bump",
      moves: "FFFF",
      deckDelta: 0,
      floorIn: F,
      floorOut: F,
      meshOffset: [0, 0, 1.5],
      meshRot: 0,
      footprint: [[0, 0], [0, 1], [0, 2], [0, 3]],
      supportCells: [[0, 0], [0, 1], [0, 2], [0, 3]],
      centerline: [
        bumpSeg(F, F + rise, -0.5, 1.5),
        bumpSeg(F + rise, F, 1.5, 3.5),
      ],
      heightSpan: [0, W + rise],
      solidId: `bump-solid-${tier}`,
      baseWeight: w,
    });
  }

  // --- wide channel --------------------------------------------------------
  // straight-wide: 1.7-wide channel centered on the cell, bulging 0.35 into
  // each flanking cell (hence the 3-cell footprint). funnel: exactly linear
  // narrow→wide taper across one cell; funnel-long = straight + funnel +
  // straight-wide fused. All reservedOnly: the feature planner emits them as
  // funnel-bracketed runs, which is what makes width continuity a
  // by-construction invariant. (curve-wide measured off-grid — its ports
  // sit on ±0.85 bbox faces and cannot mate with the 1-unit port lattice;
  // deferred to a later milestone as a set-piece macro.)
  defs.push({
    id: "straight-wide",
    family: "wide",
    moves: "F",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    widthIn: "wide",
    widthOut: "wide",
    meshOffset: [0, 0, 0],
    meshRot: 0,
    footprint: [[0, 0], [1, 0], [-1, 0]],
    supportCells: [[0, 0]],
    centerline: [line([0, F, -0.5], [0, F, 0.5])],
    heightSpan: [0, W],
    reservedOnly: true,
    columnSupports: true,
    baseWeight: 1,
  });
  defs.push({
    id: "funnel",
    family: "funnel",
    moves: "F",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    widthIn: "narrow",
    widthOut: "wide",
    meshOffset: [0, 0, 0],
    meshRot: 0,
    footprint: [[0, 0], [1, 0], [-1, 0]],
    supportCells: [[0, 0]],
    centerline: [line([0, F, -0.5], [0, F, 0.5])],
    heightSpan: [0, W],
    reservedOnly: true,
    columnSupports: true,
    baseWeight: 1,
  });
  defs.push({
    id: "funnel-long",
    family: "funnel",
    moves: "FFF",
    deckDelta: 0,
    floorIn: F,
    floorOut: F,
    widthIn: "narrow",
    widthOut: "wide",
    meshOffset: [0, 0, 1],
    meshRot: 0,
    footprint: [[0, 0], [0, 1], [0, 2], [1, 1], [-1, 1], [1, 2], [-1, 2]],
    supportCells: [[0, 0], [0, 1], [0, 2]],
    centerline: [line([0, F, -0.5], [0, F, 2.5])],
    heightSpan: [0, W],
    reservedOnly: true,
    columnSupports: true,
    baseWeight: 1,
  });

  return defs;
}

/** Reverse a def: traverse exit→entry. Gives mirrored turns and descents. */
export function reverseDef(def: PieceDef): PieceDef {
  const exit = exitPose(def);
  const hIn = opposite(exit.dir); // new travel direction at old exit
  const rho = ((hIn - 1 + 4) % 4) as Dir; // rotate old frame into new frame
  const mapP = (v: Vec3): Vec3 => {
    const [x, z] = rotP(v[0], v[2], rho);
    return [x, v[1] - def.deckDelta, z];
  };
  // Translation: rotated old-exit-port must land at (0, ·, −0.5).
  const rotExit = mapP(exit.pos);
  const t: Vec3 = [-rotExit[0], 0, -0.5 - rotExit[2]];
  const shift = (v: Vec3): Vec3 => {
    const p = mapP(v);
    return [p[0] + t[0], p[1] + t[1], p[2] + t[2]];
  };
  const shiftCell = (c: readonly [number, number]): [number, number] => {
    const [x, z] = rotP(c[0], c[1], rho);
    return [Math.round(x + t[0]), Math.round(z + t[2])];
  };
  const shiftSeg = (seg: CurveSeg): CurveSeg => {
    const s = reverseSeg(seg);
    switch (s.kind) {
      case "line":
        return { kind: "line", a: shift(s.a), b: shift(s.b) };
      case "cubic":
        return {
          kind: "cubic",
          p: [shift(s.p[0]), shift(s.p[1]), shift(s.p[2]), shift(s.p[3])],
        };
      case "arc": {
        const c = shift(s.center);
        const angleShift = -rho * Math.PI / 2;
        return {
          ...s,
          center: [c[0], 0, c[2]],
          a0: s.a0 + angleShift,
          a1: s.a1 + angleShift,
          y0: s.y0 - def.deckDelta,
          y1: s.y1 - def.deckDelta,
        };
      }
    }
  };
  const moves = def.moves.split("").reverse()
    .map((m) => m === "L" ? "R" : m === "R" ? "L" : "F").join("");
  return {
    ...def,
    moves,
    deckDelta: -def.deckDelta,
    floorIn: def.floorOut,
    floorOut: def.floorIn,
    widthIn: def.widthOut,
    widthOut: def.widthIn,
    meshOffset: shift(def.meshOffset),
    overlays: def.overlays?.map((o) => ({
      ...o,
      offset: shift(o.offset),
      rot: (((o.rot ?? 0) + rho) % 4) as Dir,
    })),
    meshRot: ((def.meshRot + rho) % 4) as Dir,
    footprint: def.footprint.map(shiftCell),
    plateSides: def.plateSides?.map(shiftCell),
    supportCells: def.supportCells.map(shiftCell).reverse(),
    centerline: def.centerline.map(shiftSeg).reverse(),
    altCenterline: def.altCenterline?.map(shiftSeg).reverse(),
    holes: def.holes?.map(shift),
    reversed: !def.reversed,
  };
}

/**
 * Mirror a def about the entry-frame x=0 plane (entry port maps to itself,
 * L↔R swap). The mesh renders with scale(−1,1,1): world = T·R(r)·Mirror, so
 * the local pre-rotation flips sign (M·R(θ) = R(−θ)·M).
 */
export function mirrorDef(def: PieceDef, id: string): PieceDef {
  const mp = (v: Vec3): Vec3 => [-v[0], v[1], v[2]];
  const mCell = (c: readonly [number, number]): [number, number] => [
    -c[0],
    c[1],
  ];
  const mSeg = (seg: CurveSeg): CurveSeg => {
    switch (seg.kind) {
      case "line":
        return { kind: "line", a: mp(seg.a), b: mp(seg.b) };
      case "cubic":
        return {
          kind: "cubic",
          p: [mp(seg.p[0]), mp(seg.p[1]), mp(seg.p[2]), mp(seg.p[3])],
        };
      case "arc":
        // x → −x maps angle θ → π − θ (cos flips, sin preserved) and
        // reverses the sweep direction, which is exactly the mirrored turn.
        return {
          ...seg,
          center: mp(seg.center),
          a0: Math.PI - seg.a0,
          a1: Math.PI - seg.a1,
        };
    }
  };
  return {
    ...def,
    id,
    // The mirrored def renders the ORIGINAL mesh flipped — its own id is not
    // a GLB node.
    meshId: def.meshId ?? def.id,
    moves: def.moves.split("")
      .map((m) => m === "L" ? "R" : m === "R" ? "L" : "F").join(""),
    meshOffset: mp(def.meshOffset),
    meshRot: ((4 - def.meshRot) % 4) as Dir,
    mirrored: !def.mirrored,
    overlays: def.overlays?.map((o) => ({
      ...o,
      offset: mp(o.offset),
      rot: ((4 - (o.rot ?? 0)) % 4) as Dir,
      mirrored: !o.mirrored,
    })),
    footprint: def.footprint.map(mCell),
    supportCells: def.supportCells.map(mCell),
    plateSides: def.plateSides?.map(mCell),
    centerline: def.centerline.map(mSeg),
    altCenterline: def.altCenterline?.map(mSeg),
    holes: def.holes?.map(mp),
  };
}

/** Walk a def's moves from the entry cell; returns per-cell [cell, dirIn]. */
export function windowCells(
  def: PieceDef,
): { cell: [number, number]; dirIn: Dir; dirOut: Dir }[] {
  const out: { cell: [number, number]; dirIn: Dir; dirOut: Dir }[] = [];
  let cx = 0, cz = 0;
  let dir: Dir = 1;
  for (const m of def.moves) {
    const dirOut: Dir = m === "F"
      ? dir
      : m === "L"
      ? turnLeft(dir)
      : turnRight(dir);
    out.push({ cell: [cx, cz], dirIn: dir, dirOut });
    dir = dirOut;
    cx += DIR_STEP[dirOut][0];
    cz += DIR_STEP[dirOut][1];
  }
  return out;
}

/** Exit pose of a def in entry frame (derived from its moves). */
export function exitPose(def: PieceDef): PortPose {
  const cells = windowCells(def);
  const last = cells[cells.length - 1];
  const [dx, dz] = DIR_STEP[last.dirOut];
  return {
    pos: [last.cell[0] + dx * 0.5, def.deckDelta, last.cell[1] + dz * 0.5],
    dir: last.dirOut,
    baseY: def.deckDelta,
  };
}

export const ENTRY_POSE: PortPose = { pos: [0, 0, -0.5], dir: 1, baseY: 0 };

export interface PieceRegistry {
  /** Forward + reversed defs, ready for window matching. */
  variants: readonly PieceDef[];
  byMoves: ReadonlyMap<string, readonly PieceDef[]>;
  /** Marble-kit decor ids usable by the decor system. */
  decorIds: readonly string[];
}

export function buildRegistry(): PieceRegistry {
  const fwd = buildDefs();
  const variants: PieceDef[] = [...fwd];
  for (const def of fwd) {
    const rev = reverseDef(def);
    // Skip reversed defs identical to forward (symmetric straights) — but a
    // width-asymmetric piece (funnel) reversed is a genuinely new variant.
    if (
      rev.moves === def.moves && rev.deckDelta === def.deckDelta &&
      rev.widthIn === def.widthIn && rev.widthOut === def.widthOut
    ) continue;
    variants.push(rev);
  }
  const byMoves = new Map<string, PieceDef[]>();
  for (const v of variants) {
    const list = byMoves.get(v.moves) ?? [];
    list.push(v);
    byMoves.set(v.moves, list);
  }
  return {
    variants,
    byMoves,
    decorIds: [
      "tree",
      "tree-tall",
      "tree-large",
      "tree-tall-large",
      "banner",
      "banner-high",
      "fan-standing-two",
      "fan-standing-four",
    ],
  };
}

/** Registry self-consistency checks (called from tests and dev builds). */
export function validateRegistry(reg: PieceRegistry): string[] {
  const errors: string[] = [];
  for (const def of reg.variants) {
    const tag = `${def.id}${def.reversed ? " (reversed)" : ""}`;
    const cells = windowCells(def);
    // moves length == supportCells length? (support cells track the channel,
    // same count as window for M1 pieces)
    if (cells.length !== def.moves.length) {
      errors.push(`${tag}: windowCells mismatch`);
    }
    // Every window cell must be inside the footprint.
    for (const c of cells) {
      if (!def.footprint.some(([x, z]) => x === c.cell[0] && z === c.cell[1])) {
        errors.push(`${tag}: window cell ${c.cell} outside footprint`);
      }
    }
    // Centerline endpoints must sit at the entry/exit ports at floor height.
    const first = def.centerline[0];
    const last = def.centerline[def.centerline.length - 1];
    const start = segPoint(first, 0);
    const end = segPoint(last, 1);
    const exit = exitPose(def);
    const expStart: Vec3 = [0, def.floorIn, -0.5];
    const expEnd: Vec3 = [
      exit.pos[0],
      def.deckDelta + def.floorOut,
      exit.pos[2],
    ];
    for (let i = 0; i < 3; i++) {
      if (Math.abs(start[i] - expStart[i]) > 1e-6) {
        errors.push(`${tag}: centerline start ${start} != ${expStart}`);
        break;
      }
    }
    for (let i = 0; i < 3; i++) {
      if (Math.abs(end[i] - expEnd[i]) > 1e-6) {
        errors.push(`${tag}: centerline end ${end} != ${expEnd}`);
        break;
      }
    }
    // Alt branch (diamonds): same ports as the main centerline.
    if (def.altCenterline) {
      const aStart = segPoint(def.altCenterline[0], 0);
      const aEnd = segPoint(def.altCenterline[def.altCenterline.length - 1], 1);
      for (let i = 0; i < 3; i++) {
        if (Math.abs(aStart[i] - expStart[i]) > 1e-6) {
          errors.push(`${tag}: altCenterline start ${aStart} != ${expStart}`);
          break;
        }
      }
      for (let i = 0; i < 3; i++) {
        if (Math.abs(aEnd[i] - expEnd[i]) > 1e-6) {
          errors.push(`${tag}: altCenterline end ${aEnd} != ${expEnd}`);
          break;
        }
      }
    }

    // Centerline segment chaining (C0).
    for (let s = 1; s < def.centerline.length; s++) {
      const a = segPoint(def.centerline[s - 1], 1);
      const b = segPoint(def.centerline[s], 0);
      if (Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) > 1e-6) {
        errors.push(`${tag}: centerline gap at segment ${s}`);
      }
    }
    // Reversing twice must round-trip.
    if (!def.reversed) {
      const twice = reverseDef(reverseDef(def));
      if (
        twice.moves !== def.moves ||
        Math.abs(twice.deckDelta - def.deckDelta) > 1e-9 ||
        JSON.stringify(twice.footprint.slice().sort()) !==
          JSON.stringify(def.footprint.slice().sort())
      ) {
        errors.push(`${tag}: reverse(reverse(def)) != def`);
      }
    }
  }
  return errors;
}

// Re-export for consumers that only need the label helper.
export { moveLabel };
